import { ApiError, apiJson } from './apiFetch';
import { getAuthClient, getRefreshToken, saveSession, type SessionPayload } from './authSession';
import { normalizeUserRole, type UserProfile } from './user';

export type LookupMethod = 'password' | 'sso';

export interface LookupResult {
  method: LookupMethod;
  provider?: string;
}

export interface AuthSessionResponse {
  user: UserProfile;
  session: SessionPayload;
  mfa?: { enrolled: boolean; required: boolean };
}

export const PASSWORD_MIN_LENGTH = 10;

// Error codes khova-api returns on the auth endpoints (contract §5). Anything else is shown
// with the generic sign-in failure copy.
export const AUTH_ERROR_CODES = [
  'invalid-credentials',
  'account-suspended',
  'user-deactivated',
  'kit-not-allowed',
  'rate-limited',
  'invalid-or-expired-link',
] as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number] | 'sign-in-failed' | 'sign-in-unavailable';

/** Maps any failure of an auth call to a code the screens have a translation for. */
export const toAuthErrorCode = (err: unknown): AuthErrorCode => {
  if (!(err instanceof ApiError)) {
    return 'sign-in-unavailable';
  }
  if (err.status === 429) {
    return 'rate-limited';
  }
  const known = AUTH_ERROR_CODES.find((code) => code === err.code);
  return known ?? 'sign-in-failed';
};

// Auth endpoints answer 401 for bad credentials; that must never trigger a session refresh.
const AUTH_REQUEST_OPTIONS = { shouldRefreshOnUnauthorized: false };

const postAuth = <T>(path: string, body: Record<string, unknown>): Promise<T> =>
  apiJson<T>(path, { method: 'POST', body: JSON.stringify(body) }, AUTH_REQUEST_OPTIONS);

const completeSignIn = async (response: AuthSessionResponse): Promise<UserProfile> => {
  await saveSession(response.session);
  return { ...response.user, role: normalizeUserRole(response.user.role) };
};

export const lookupEmail = (email: string): Promise<LookupResult> => postAuth<LookupResult>('/auth/lookup', { email });

export const loginWithPassword = async (email: string, password: string): Promise<UserProfile> =>
  completeSignIn(await postAuth<AuthSessionResponse>('/auth/login', { email, password, client: getAuthClient() }));

export const requestPasswordReset = async (email: string): Promise<void> => {
  await postAuth<{ ok: true }>('/auth/password/forgot', { email, client: getAuthClient() });
};

export const resetPassword = async (tokenHash: string, password: string): Promise<UserProfile> =>
  completeSignIn(
    await postAuth<AuthSessionResponse>('/auth/password/reset', {
      tokenHash,
      type: 'recovery',
      password,
      client: getAuthClient(),
    }),
  );

export type InviteLinkType = 'invite' | 'magiclink';

export const acceptInvite = async (tokenHash: string, type: InviteLinkType, password: string): Promise<UserProfile> =>
  completeSignIn(
    await postAuth<AuthSessionResponse>('/auth/invite/accept', {
      tokenHash,
      type,
      password,
      client: getAuthClient(),
    }),
  );

/** Revokes the Khova session server-side (web: also clears the cookies). */
export const logoutKhova = async (): Promise<void> => {
  const refreshToken = await getRefreshToken();
  await apiJson<{ ok: true }>(
    '/auth/logout',
    { method: 'POST', body: JSON.stringify({ client: getAuthClient(), ...(refreshToken && { refreshToken }) }) },
    AUTH_REQUEST_OPTIONS,
  );
};
